import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Tag, ToggleLeft } from 'lucide-react';

export default function CouponManagement() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    code: '', discount_type: 'percentage', discount_value: 10,
    min_cart_value: 0, expiry_date: '', max_usage: 100, is_active: true
  });

  useEffect(() => { loadCoupons(); }, []);

  async function loadCoupons() {
    try { const { data } = await api.get('/admin/coupons'); setCoupons(data); } catch {} finally { setLoading(false); }
  }

  function openCreate() {
    setEditing(null);
    setForm({ code: '', discount_type: 'percentage', discount_value: 10, min_cart_value: 0, expiry_date: '', max_usage: 100, is_active: true });
    setDialogOpen(true);
  }

  function openEdit(c) {
    setEditing(c);
    setForm({
      code: c.code, discount_type: c.discount_type, discount_value: c.discount_value,
      min_cart_value: c.min_cart_value || 0, expiry_date: c.expiry_date ? c.expiry_date.split('T')[0] : '',
      max_usage: c.max_usage || 100, is_active: c.is_active
    });
    setDialogOpen(true);
  }

  async function handleSave() {
    if (!form.code.trim()) { toast.error('Code is required'); return; }
    const payload = {
      ...form,
      code: form.code.toUpperCase().trim(),
      expiry_date: form.expiry_date ? new Date(form.expiry_date).toISOString() : ''
    };
    try {
      if (editing) {
        await api.put(`/admin/coupons/${editing.id}`, payload);
        toast.success('Updated');
      } else {
        await api.post('/admin/coupons', payload);
        toast.success('Coupon created');
      }
      setDialogOpen(false);
      loadCoupons();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  }

  async function handleToggle(id) {
    try {
      const { data } = await api.put(`/admin/coupons/${id}/toggle`);
      toast.success(data.is_active ? 'Coupon activated' : 'Coupon deactivated');
      loadCoupons();
    } catch { toast.error('Failed'); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this coupon?')) return;
    try { await api.delete(`/admin/coupons/${id}`); toast.success('Deleted'); loadCoupons(); } catch { toast.error('Failed'); }
  }

  return (
    <div data-testid="coupon-management">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-['Outfit'] text-2xl font-semibold text-[#0A0A0A]">Coupons & Promotions</h1>
        <Button className="bg-[#7551e8] hover:bg-[#5e3dc7] text-white" onClick={openCreate} data-testid="create-coupon-btn">
          <Plus size={16} className="mr-2" /> Create Coupon
        </Button>
      </div>

      <Card className="border border-zinc-200 bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Value</TableHead>
              <TableHead>Min Cart</TableHead>
              <TableHead>Usage</TableHead>
              <TableHead>Expiry</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[120px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8">Loading...</TableCell></TableRow>
            ) : coupons.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-[#52525B]">No coupons created</TableCell></TableRow>
            ) : coupons.map(c => (
              <TableRow key={c.id} data-testid={`coupon-row-${c.id}`}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Tag size={14} className="text-[#7551e8]" />
                    <span className="font-mono font-medium">{c.code}</span>
                  </div>
                </TableCell>
                <TableCell className="text-sm capitalize">{c.discount_type}</TableCell>
                <TableCell className="font-medium">{c.discount_type === 'percentage' ? `${c.discount_value}%` : `Rs.${c.discount_value}`}</TableCell>
                <TableCell className="text-sm">Rs.{c.min_cart_value || 0}</TableCell>
                <TableCell className="text-sm">{c.usage_count || 0}/{c.max_usage}</TableCell>
                <TableCell className="text-sm">{c.expiry_date ? new Date(c.expiry_date).toLocaleDateString() : 'No expiry'}</TableCell>
                <TableCell>
                  <Badge className={c.is_active ? 'bg-green-100 text-green-700 border-0' : 'bg-red-100 text-red-700 border-0'}>
                    {c.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleToggle(c.id)}
                      className={c.is_active ? 'text-red-500' : 'text-green-600'} data-testid={`toggle-coupon-${c.id}`}>
                      <ToggleLeft size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`edit-coupon-${c.id}`}><Pencil size={14} /></Button>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(c.id)} data-testid={`delete-coupon-${c.id}`}><Trash2 size={14} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-['Outfit']">{editing ? 'Edit Coupon' : 'Create Coupon'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Coupon Code</Label>
              <Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="e.g., DIWALI50" className="font-mono" data-testid="coupon-code-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Discount Type</Label>
                <Select value={form.discount_type} onValueChange={v => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger data-testid="coupon-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="flat">Flat (Rs.)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Discount Value</Label>
                <Input type="number" value={form.discount_value} onChange={e => setForm({ ...form, discount_value: Number(e.target.value) })}
                  data-testid="coupon-value-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Min Cart Value (Rs.)</Label>
                <Input type="number" value={form.min_cart_value} onChange={e => setForm({ ...form, min_cart_value: Number(e.target.value) })} />
              </div>
              <div>
                <Label>Max Usage</Label>
                <Input type="number" value={form.max_usage} onChange={e => setForm({ ...form, max_usage: Number(e.target.value) })} />
              </div>
            </div>
            <div>
              <Label>Expiry Date</Label>
              <Input type="date" value={form.expiry_date} onChange={e => setForm({ ...form, expiry_date: e.target.value })} data-testid="coupon-expiry-input" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} data-testid="coupon-active-switch" />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#7551e8] hover:bg-[#5e3dc7] text-white" onClick={handleSave} data-testid="save-coupon-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
